import { Dictionary, difference, isUndefined, merge } from 'lodash'
import { DeltaOperation, DeltaStatic, StringMap } from 'quill'
import { Quill } from 'react-quill'
import { SOLUTION_ENGINE_ERROR_TYPE } from '../constants/solutionEngineErrorType'
import {
	latexENotationRegEx,
	latexNumberRegEx,
	latexNumberWithSeparatorsRegEx,
	latexVariableRegEx,
	variableBracketRegEx
} from '../constants/variable'
import { DiffObject } from '../types/DiffObject'
import { VARIABLE_TYPE, VariableState } from '../types/Variable'
import { extractVariableFromBrackets, renameVariablesInLatex, replaceVariableValuesInLatex } from './variable'

const Delta = Quill.import('delta')

export const isOpString = (op: DeltaOperation) => op.insert && typeof op.insert === 'string'

export const isOpVariable = (op: DeltaOperation) => op.insert && op.insert.variable

export const isOpFormula = (op: DeltaOperation) => op.insert && op.insert.formula

export const createVariableOp = (name: string): DeltaOperation => {
	return {
		insert: { variable: name }
	}
}

/**
 * Check if the contents exist and are empty, ignoring the new line Quill inserts after editing
 */
export const areContentsEmpty = (contents: DeltaStatic | null | undefined) =>
	!contents ||
	!contents.ops ||
	contents.ops.length === 0 ||
	(contents.ops.length === 1 && (contents.ops[0].insert === '\n' || contents.ops[0].insert === ''))

interface StringPart {
	isVariable: boolean
	text: string
	start: number
	end: number
}

/**
 * Convert bracket variables to `VariableBlot`s.
 *
 * @param contents The contents of quill.
 * @param isSnapshot boolean, if true do not allow using the double brackets to create new variables
 * @param existingVariables Existing variables, if any.
 *
 * @returns A new full `Delta`, or `undefined` if no updates are required
 */
export const findAndReplaceBracketVariables = (
	contents: DeltaStatic,
	isSnapshot: boolean,
	existingVariables?: VariableState[]
) => {
	let hasUpdate = false
	const newContents: DeltaStatic = new Delta()
	newContents.ops = contents.reduce((acc: DeltaOperation[], op: DeltaOperation) => {
		if (!isOpString(op)) {
			acc.push(op)
			return acc
		}
		const value = op.insert as string
		const matches = value.match(variableBracketRegEx)
		if (!matches) {
			acc.push(op)
			return acc
		}
		matches.reduce((matchParts: StringPart[], m, i) => {
			const prev: StringPart | undefined = matchParts.length > 0 ? matchParts[matchParts.length - 1] : undefined
			const index = value.indexOf(m, prev ? prev.end : 0)
			const part = {
				isVariable: true,
				text: m,
				start: index,
				end: index + m.length
			}

			// if first match is not at start of the string, preserve text before
			if (i === 0 && part.start > 0) {
				acc.push({ insert: value.substring(0, part.start) })
			}

			// if there was a gap between matches, preserve text between
			if (prev && prev.end < part.start) {
				acc.push({ insert: value.substring(prev.end, part.start) })
			}

			// track match part with position
			matchParts.push(part)
			// extract and add the variable if new
			const { variable, isNew } = extractVariableFromBrackets(part.text, existingVariables)
			// snapshots support converting existing variables but not new ones
			if (!isSnapshot || (isSnapshot && !isNew)) acc.push(createVariableOp(variable.name))
			if (isSnapshot && isNew) acc.push({ insert: part.text })

			// for the last match, preserve text after
			if (i === matches.length - 1 && part.end < value.length) {
				acc.push({ insert: value.substring(part.end, value.length) })
			}
			return matchParts
		}, [])
		hasUpdate = true
		return acc
	}, [] as DeltaOperation[])
	if (!hasUpdate) {
		return undefined
	}

	return newContents
}

/**
 * If variables are renamed, rename them in the contents and return an updated statement contents.
 *
 * @param contents The contents of quill.
 * @param renamedVariables The variable changes from being renamed.
 *
 * @returns A new full `Delta`, or `undefined` if no changes were made.
 */
export const renameVariables = (contents: DeltaStatic, renamedVariables: DiffObject<VariableState>[]) => {
	if (renamedVariables.length <= 0) {
		return undefined
	}
	let didUpdate = false
	const newContents: DeltaStatic = new Delta()
	newContents.ops = contents.map(op => {
		// rename variables
		if (isOpVariable(op)) {
			const variableName = op.insert.variable as string
			const renamedVariable = renamedVariables.filter(v => v.from.name === variableName)[0]
			if (!renamedVariable) {
				return op
			}
			const newOp = merge({}, op, {
				insert: { variable: renamedVariable.to.name }
			})
			didUpdate = true
			return newOp
		}
		// rename variables inside of formulas
		if (isOpFormula(op)) {
			const formula = op.insert.formula as string
			const newFormula = renameVariablesInLatex(formula, renamedVariables)
			if (newFormula === formula) {
				return op
			}
			const newOp = merge({}, op, {
				insert: { formula: newFormula }
			})
			didUpdate = true
			return newOp
		}
		return op
	})
	if (!didUpdate) {
		return undefined
	}
	return newContents
}

/**
 * Fixes the weird bug given:
 * * Contents begins with a `VariableBlot`
 * * User selects the first line of content
 * * User begins typing, replacing the selection
 *
 * @param contents The contents of quill.
 *
 *  * @returns A new full `Delta`, or `undefined` if no changes were made.
 */
export const fixWeirdStartingVariableReplacement = (contents: DeltaStatic) => {
	if (
		contents.ops &&
		contents.ops[0] &&
		contents.ops[0].attributes &&
		contents.ops[0].attributes.background === '#7bcf64'
	) {
		return new Delta(
			contents.ops.map((op: DeltaOperation, idx: number) => {
				if (idx === 0) {
					return { insert: op.insert }
				}
				return op
			})
		)
	}
	return undefined
}

/** If the formula is a basic variable format that does not require FormulaBlot / mathlive, render it as plain text */
const simplifyFormulaOp = (latex: string) => {
	if (latexNumberRegEx.exec(latex)) {
		latexNumberRegEx.lastIndex = 0
		return { insert: latex.replace('\\%', '%') }
	} else if (latexNumberWithSeparatorsRegEx.exec(latex)) {
		latexNumberWithSeparatorsRegEx.lastIndex = 0
		return { insert: latex.replace('\\$', '$') }
	} else if (latexENotationRegEx.exec(latex)) {
		latexENotationRegEx.lastIndex = 0
		return { insert: latex }
	} else {
		return { insert: { formula: latex } }
	}
}

/**
 * Populates variables in the contents with the corresponding value.
 *
 * @param contents The contents of quill.
 * @param variableValues A JSON object string of variable name keys and values.
 *
 * @returns A new full `Delta`.
 */
export const populateVariableValues = (contents: DeltaStatic, variableValues: string) => {
	const variableValuesDictionary = JSON.parse(variableValues) as Dictionary<string>
	// construct a diff for the remove and insert of the variable ops
	const diff: DeltaStatic = new Delta()
	diff.ops = contents.reduce((acc: DeltaOperation[], op: DeltaOperation, _idx: number, _arr: DeltaOperation[]) => {
		if (isOpFormula(op)) {
			const newLatex = replaceVariableValuesInLatex(op.insert.formula, variableValuesDictionary)
			acc.push({ delete: 1 })
			acc.push({ ...simplifyFormulaOp(newLatex), attributes: op.attributes })
			return acc
		}
		if (!isOpVariable(op)) {
			acc.push({ retain: typeof op.insert === 'string' ? op.insert.length : 1 })
			return acc
		}
		const variableName = op.insert.variable as string
		const variableValue = variableValuesDictionary[variableName]
		if (
			isUndefined(variableValue) ||
			variableValue === '' ||
			// do not replace if the formatted value is an error type
			Object.values(SOLUTION_ENGINE_ERROR_TYPE)
				.map(v => v as string)
				.includes(variableValue)
		) {
			acc.push({ retain: 1 })
			return acc
		}
		acc.push({ delete: 1 })
		acc.push({ ...simplifyFormulaOp(variableValue), attributes: op.attributes })
		return acc
	}, [])
	// use `compose` to apply the diff to the original delta
	return contents.compose(diff)
}

/**
 * Populates rich text variables in the contents with the corresponding variable contents.
 *
 * @param contents The contents of quill.
 * @param variableContentsDictionary A dictionary of contents of quill from each variable.
 * @param variableValues A JSON object string of variable name keys and values.
 * @returns A new full `Delta`.
 */
export const populateRichTextVariableValues = (
	contents: DeltaStatic,
	variableContentsDictionary: Dictionary<DeltaStatic | boolean | undefined>,
	variableValues: string
) => {
	const variableValuesDictionary = variableValues ? (JSON.parse(variableValues) as Dictionary<string>) : {}
	// construct a diff for the remove and insert of the variable ops
	const diff: DeltaStatic = new Delta()
	diff.ops = contents.reduce((acc: DeltaOperation[], op: DeltaOperation, _idx: number, _arr: DeltaOperation[]) => {
		if (!isOpVariable(op)) {
			acc.push({ retain: typeof op.insert === 'string' ? op.insert.length : 1 })
			return acc
		}
		const variableName = op.insert.variable as string
		const variableContents = variableContentsDictionary[variableName]
		if (!variableContents) {
			acc.push({ retain: 1 })
			return acc
		}
		const variableContentOps = (variableContents as DeltaStatic).ops
		if (!variableContentOps) {
			acc.push({ retain: 1 })
			return acc
		}
		acc.push({ delete: 1 })
		variableContentOps.forEach((op: DeltaOperation, index: number) => {
			// allow replacing variable values in rich text variable formulas
			if (isOpFormula(op)) {
				const newLatex = replaceVariableValuesInLatex(op.insert.formula, variableValuesDictionary)
				acc.push({ ...simplifyFormulaOp(newLatex), attributes: op.attributes })
				return
			}
			const isList =
				op.attributes?.list === 'alpha' || op.attributes?.list === 'bullet' || op.attributes?.list === 'ordered'
			// if the last variableContent op is a new line or ends with a new line, remove it
			// unless it's part of a list
			if (
				index === variableContentOps.length - 1 &&
				typeof op.insert === 'string' &&
				op.insert.endsWith('\n') &&
				!isList
			) {
				if (op.insert === '\n') return
				acc.push(merge({}, op, { insert: op.insert.slice(0, op.insert.length - 1) }))
				return
			}
			acc.push(op)
		})
		return acc
	}, [])
	// use `compose` to apply the diff to the original delta
	return contents.compose(diff)
}

/**
 * Searches the contents to find all variable names that are referenced.
 *
 * @param contents The contents of quill.
 *
 * @returns An array of variable names, if any.
 */
export const getVariableNames = (contents: DeltaStatic) => {
	const variableNames: string[] = []
	contents.forEach(op => {
		if (isOpVariable(op)) {
			const variableName = op.insert.variable as string
			if (!variableNames.includes(variableName)) {
				variableNames.push(variableName)
			}
		}
		if (isOpFormula(op)) {
			// find the first \variable, if any
			let match = latexVariableRegEx.exec(op.insert.formula)

			// start looping \variable matches
			while (match) {
				const matchVariableName = match[1]
				if (!variableNames.includes(matchVariableName)) {
					variableNames.push(matchVariableName)
				}
				// find the next \variable, if any
				match = latexVariableRegEx.exec(op.insert.formula)
			}
		}
	})
	return variableNames.sort((a, b) => a.localeCompare(b))
}

/**
 * Search the contents of quill to find all variable names that are referenced, with empty parameters.
 *
 * @param contents The contents of quill.
 * @param variables The current variables.
 *
 * @return An array of variable names, if any
 */
export const getEmptyParamVariableNames = (contents: DeltaStatic, variables?: VariableState[]): string[] => {
	const variableNames = getVariableNames(contents)
	return (
		variables?.reduce((it, v) => {
			if (variableNames.includes(v.name) && !v.parameters && v.variableType !== VARIABLE_TYPE.RICH_TEXT) {
				return [...it, v.name]
			}
			return it
		}, [] as string[]) ?? []
	)
}

/**
 * Search the contents of quill to find all variable names that are referenced, which are undefined.
 *
 * @param contents The contents of quill.
 * @param variables The current variables.
 *
 * @return An array of variable names, if any
 */
export const getUndefinedVariableNames = (contents: DeltaStatic, variables?: VariableState[]): string[] => {
	const variableNames = getVariableNames(contents)
	const definedVariableNames = variables?.map(v => v.name) ?? []
	const undefinedVariableNames = difference(variableNames, definedVariableNames)
	return undefinedVariableNames
}

/**
 * Find whether there are any variables or formulas in the provided quill contents.
 *
 * @param contents The contents of quill.
 *
 * @returns True if there are any variables or formulas
 */
export const hasVariablesOrFormulas = (contents: DeltaStatic) =>
	!!contents.ops && contents.ops.filter(op => isOpFormula(op) || isOpVariable(op)).length > 0

/**
 * Find whether there are any rich text variables in formulas in the provided quill contents.
 *
 * @param contents The contents of quill.
 *
 * @returns True if there are any rich text variables in formulas
 */
export const hasRichTextVariablesInFormulas = (contents: DeltaStatic, variables?: VariableState[]) =>
	!!contents.ops &&
	contents.ops
		.filter(op => isOpFormula(op))
		.some(op => {
			const variableNames = getVariableNames(new Delta([op]))
			return variables?.some(v => variableNames.includes(v.name) && v.variableType === VARIABLE_TYPE.RICH_TEXT)
		})

const createShadowContainer = () => {
	const shadowContainer = document.createElement('pre')
	shadowContainer.classList.add('visually-hidden')
	document.body.append(shadowContainer)
	return shadowContainer
}

const createShadowQuillInstance = (shadowContainer: HTMLPreElement) => {
	const quill = new Quill(shadowContainer, {
		readOnly: true,
		modules: { table: true, toolbar: false, clipboard: { matchVisual: false } }
	})
	return quill
}

export const convertHtmlToContents = (html: string) => {
	// create a shadow quill instance to convert the html into a delta
	const shadowContainer = createShadowContainer()
	shadowContainer.innerHTML = html
	const quill = createShadowQuillInstance(shadowContainer)
	const quillContents = quill.getContents()
	// cleanup
	shadowContainer.remove()
	return quillContents
}

export const convertContentsToHtml = (quillContents: DeltaStatic | null | undefined) => {
	if (!quillContents) return null
	// create a shadow quill instance to convert the delta into html
	const shadowContainer = createShadowContainer()
	const quill = createShadowQuillInstance(shadowContainer)
	quill.setContents(quillContents)
	// get html and remove the mathlive auto-generated markup from inside variable and formula spans
	const html = quill.root.innerHTML
		.replace(/\ufeff<span contenteditable.*?\ufeff/gm, '')
		.replace(/ ?style="font-size: [\d.]+rem;"| ?style=""/gm, '')
		.replace(/ ?plain-text/gm, '')
		.replace(/ ?math-scale-\d/gm, '')
	// cleanup
	shadowContainer.remove()
	return html
}

export const convertContentsToPlainText = (quillContents: DeltaStatic | null | undefined) => {
	if (!quillContents) return null
	// create a shadow quill instance to convert the delta into html
	const shadowContainer = createShadowContainer()
	const quill = createShadowQuillInstance(shadowContainer)
	quill.setContents(quillContents)
	const text = quill.getText()
	// cleanup
	shadowContainer.remove()
	return text
}

/** Condense the delta into a single line */
export const condensedDelta = (quillContents: DeltaStatic) => {
	const condensed = Array<DeltaOperation>()
	quillContents?.eachLine((line: DeltaStatic, _: StringMap) => {
		line.ops?.forEach((op: DeltaOperation) => {
			condensed.push(op)
		})
		condensed.push({ insert: ' ' })
	})
	return new Delta(condensed.slice(0, -1))
}
